import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { 
  FileText, 
  ArrowRight,
  Stethoscope,
  Building2,
  Shield,
  LogIn,
  Eye,
  EyeOff,
  AlertCircle,
  Loader2,
  CheckCircle,
  Users,
  ClipboardList,
  Gavel,
  BarChart3,
  Zap
} from "lucide-react";

const ADMIN_TYPES = [
  {
    type: "provider",
    name: "Provider Admin",
    icon: Stethoscope,
    color: "emerald",
    email: "provider@demo.preauthpro.com",
    password: "demo123",
    description: "Hospital / Clinic administrators managing PA submissions",
    features: [
      "Submit & track PA requests",
      "Patient management",
      "AI-powered ICD/CPT coding",
      "Eligibility verification",
      "Denial management & appeals"
    ]
  },
  {
    type: "tpa",
    name: "TPA Admin",
    icon: Building2,
    color: "blue",
    email: "tpa@demo.preauthpro.com",
    password: "demo123",
    description: "Third Party Administrator reviewing PA requests from providers",
    features: [
      "Centralized review queue",
      "Case assignment & tracking",
      "Multi-provider management",
      "SLA monitoring",
      "278 EDI support"
    ]
  },
  {
    type: "payer",
    name: "Payor Admin",
    icon: Shield,
    color: "purple",
    email: "payer@demo.preauthpro.com",
    password: "demo123",
    description: "Insurance company administrators with final PA decision authority",
    features: [
      "Final approval/denial authority",
      "Medical necessity review",
      "Policy compliance checking",
      "Appeals management",
      "278 EDI response generation"
    ]
  }
];

const colorMap = {
  emerald: {
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/30",
    text: "text-emerald-400",
    icon: "bg-emerald-500/20",
    button: "bg-emerald-600 hover:bg-emerald-500",
    ring: "ring-emerald-500/40",
    glow: "shadow-emerald-500/20"
  },
  blue: {
    bg: "bg-blue-500/10",
    border: "border-blue-500/30",
    text: "text-blue-400",
    icon: "bg-blue-500/20",
    button: "bg-blue-600 hover:bg-blue-500",
    ring: "ring-blue-500/40",
    glow: "shadow-blue-500/20"
  },
  purple: {
    bg: "bg-purple-500/10",
    border: "border-purple-500/30",
    text: "text-purple-400",
    icon: "bg-purple-500/20",
    button: "bg-purple-600 hover:bg-purple-500",
    ring: "ring-purple-500/40",
    glow: "shadow-purple-500/20"
  }
};

export default function LandingPage() {
  const { loginWithPassword, register, user } = useAuth();
  const navigate = useNavigate();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [authTab, setAuthTab] = useState("login");
  const [registerName, setRegisterName] = useState("");
  const [registerOrgName, setRegisterOrgName] = useState("");

  const [wizardStep, setWizardStep] = useState(1);
  const [wizardData, setWizardData] = useState({
    customer_type: "Independent Practice",
    organization_name: "",
    dba_name: "",
    organization_type: "provider",
    tax_id: "",
    organization_npi: "",
    website: "",
    organization_phone: "",
    organization_fax: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip_code: "",
    country: "US",
    primary_admin_first_name: "",
    primary_admin_last_name: "",
    primary_admin_title: "",
    primary_admin_email: "",
    primary_admin_phone: "",
    practice_type: "Medical",
    specialty: "",
    provider_count: 1,
    location_count: 1,
    front_office_count: 0,
    billing_staff_count: 0,
    estimated_patients_per_day: 10,
    estimated_monthly_eligibility: 100,
    estimated_monthly_pa: 10,
    practice_management_system: "Other",
    ehr_system: "Other",
    clearinghouse: "Other",
    eligibility_vendor: "Other",
    pa_workflow: "Other",
    requested_integrations: [],
    requested_modules: [],
    go_live_date: "",
    demo_date: "",
    notes: "",
    terms_accepted: false,
    privacy_accepted: false,
    baa_required: false,
    marketing_consent: false
  });
  const [wizardSubmitting, setWizardSubmitting] = useState(false);
  const [wizardSubmitted, setWizardSubmitted] = useState(false);

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!wizardData.organization_name.trim()) {
          toast.error("Legal Organization Name is required.");
          return false;
        }
        if (!wizardData.organization_phone.trim()) {
          toast.error("Organization Phone Number is required.");
          return false;
        }
        return true;
      case 2:
        if (!wizardData.address_line1.trim()) {
          toast.error("Address Line 1 is required.");
          return false;
        }
        if (!wizardData.city.trim()) {
          toast.error("City is required.");
          return false;
        }
        if (!wizardData.state.trim()) {
          toast.error("State is required.");
          return false;
        }
        if (!wizardData.zip_code.trim()) {
          toast.error("ZIP Code is required.");
          return false;
        }
        if (!wizardData.country.trim()) {
          toast.error("Country is required.");
          return false;
        }
        return true;
      case 3:
        if (!wizardData.primary_admin_first_name.trim()) {
          toast.error("Administrator First Name is required.");
          return false;
        }
        if (!wizardData.primary_admin_last_name.trim()) {
          toast.error("Administrator Last Name is required.");
          return false;
        }
        if (!wizardData.primary_admin_email.trim()) {
          toast.error("Administrator Email is required.");
          return false;
        }
        if (!/\S+@\S+\.\S+/.test(wizardData.primary_admin_email)) {
          toast.error("Please enter a valid email address.");
          return false;
        }
        if (!wizardData.primary_admin_phone.trim()) {
          toast.error("Administrator Mobile Phone is required.");
          return false;
        }
        return true;
      case 4:
        if (!wizardData.practice_type) {
          toast.error("Practice Type is required.");
          return false;
        }
        if (wizardData.provider_count <= 0) {
          toast.error("Number of Providers must be at least 1.");
          return false;
        }
        if (wizardData.location_count <= 0) {
          toast.error("Number of Locations must be at least 1.");
          return false;
        }
        if (wizardData.estimated_patients_per_day < 0) {
          toast.error("Estimated patients per day cannot be negative.");
          return false;
        }
        if (wizardData.estimated_monthly_eligibility < 0) {
          toast.error("Estimated monthly eligibility transactions cannot be negative.");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleWizardSubmit = async (e) => {
    e.preventDefault();
    if (!wizardData.terms_accepted || !wizardData.privacy_accepted || !wizardData.baa_required) {
      toast.error("Please accept the Terms, Privacy Policy, and HIPAA BAA.");
      return;
    }
    setWizardSubmitting(true);
    try {
      await axios.post(`${API}/auth/register-wizard`, wizardData, { withCredentials: true });
      toast.success("Registration request submitted successfully.");
      setWizardSubmitted(true);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to submit registration. Please try again.");
    } finally {
      setWizardSubmitting(false);
    }
  };

  const handleDemoLogin = (admin) => {
    setSelectedType(admin.type);
    setLoginEmail("");
    setLoginPassword("");
    setRegisterName("");
    setRegisterOrgName("");
    setLoginError("");
    setAuthTab("login");
    setShowLoginModal(true);
  };

  const handleLogin = async (e) => {
    e?.preventDefault();
    setLoginError("");
    setLoginLoading(true);
    const result = await loginWithPassword(loginEmail, loginPassword);
    setLoginLoading(false);
    if (result.success) {
      setShowLoginModal(false);
      navigate("/pa-requests");
    } else {
      setLoginError(result.error);
    }
  };

  const handleRegister = async (e) => {
    e?.preventDefault();
    setLoginError("");
    setLoginLoading(true);
    const orgType = selectedType || "provider";
    const result = await register(loginEmail, loginPassword, registerName, orgType, registerOrgName);
    setLoginLoading(false);
    if (result.success) {
      setShowLoginModal(false);
      navigate("/pa-requests");
    } else {
      setLoginError(result.error);
    }
  };

  const openLoginModal = (type) => {
    setSelectedType(type);
    setLoginEmail("");
    setLoginPassword("");
    setRegisterName("");
    setRegisterOrgName("");
    setLoginError("");
    setAuthTab("login");
    setShowLoginModal(true);
  };

  useEffect(() => {
    if (user) {
      navigate("/pa-requests");
    }
  }, [user, navigate]);

  if (user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a] flex flex-col">
      {/* Header */}
      <header className="border-b border-slate-800/50 bg-[#0d1321]/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-500 flex items-center justify-center">
                <FileText className="w-5 h-5 text-slate-900" />
              </div>
              <div>
                <span className="font-bold text-lg text-white tracking-tight">Priorath</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                onClick={() => { setAuthTab("login"); setShowLoginModal(true); }}
                className="text-slate-300 hover:text-white text-xs font-semibold"
              >
                Sign In
              </Button>
              <Button
                onClick={() => { setAuthTab("register"); setWizardStep(1); setShowLoginModal(true); }}
                className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold px-4 py-2 rounded-lg"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-5xl w-full">
          {/* Title Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-8">
              Select Your Admin Type
            </h1>
          </div>

          {/* Admin Type Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {ADMIN_TYPES.map((admin) => {
              const colors = colorMap[admin.color];
              const Icon = admin.icon;
              return (
                <Card 
                  key={admin.type}
                  className={`bg-slate-900/60 ${colors.border} hover:shadow-xl hover:${colors.glow} transition-all duration-300 group cursor-pointer`}
                  data-testid={`admin-card-${admin.type}`}
                  onClick={() => handleDemoLogin(admin)}
                >
                  <CardContent className="p-6">
                    {/* Icon & Title */}
                    <div className="flex items-center gap-4 mb-4">
                      <div className={`w-14 h-14 rounded-xl ${colors.icon} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                        <Icon className={`w-7 h-7 ${colors.text}`} />
                      </div>
                      <div>
                        <h3 className={`text-xl font-bold ${colors.text}`}>{admin.name}</h3>
                      </div>
                    </div>

                    <p className="text-sm text-slate-400 mb-4 leading-relaxed">{admin.description}</p>

                    {/* Features */}
                    <ul className="space-y-2 mb-5">
                      {admin.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-xs text-slate-500">
                          <CheckCircle className={`w-3.5 h-3.5 ${colors.text} flex-shrink-0`} />
                          {feature}
                        </li>
                      ))}
                    </ul>

                    {/* Login Button */}
                    <Button 
                      className={`w-full ${colors.button} text-white font-semibold`}
                      data-testid={`demo-login-${admin.type}`}
                      disabled={loginLoading}
                    >
                      {loginLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <ArrowRight className="w-4 h-4 mr-2" />
                      )}
                      Enter as {admin.name}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800/50 py-4">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-xs text-slate-600">
            Priorath Sandbox Environment &mdash; sandbox.priorath.com &mdash; Dev Branch
          </p>
        </div>
      </footer>

      {/* Login/Register Dialog */}
      <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
        <DialogContent 
          className={`bg-slate-900 border-slate-700 ${authTab === "login" ? "sm:max-w-md" : "sm:max-w-2xl"} w-full transition-all duration-300 max-h-[90vh] overflow-y-auto`} 
          data-testid="auth-dialog"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-400" />
              Priorath - {authTab === "login" ? "Sign In" : `Get Started (Step ${wizardStep} of 8)`}
            </DialogTitle>
          </DialogHeader>

          <Tabs value={authTab} onValueChange={setAuthTab} className="w-full">
            <TabsList className="w-full bg-slate-800 border border-slate-700">
              <TabsTrigger value="login" className="flex-1 data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400">
                Sign In
              </TabsTrigger>
              <TabsTrigger value="register" className="flex-1 data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400">
                Get Started
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-4">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email" className="text-slate-300">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="admin@example.com"
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="login-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="login-password" className="text-slate-300">Password</Label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showPassword ? "text" : "password"}
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      placeholder="Enter password"
                      className="bg-slate-800 border-slate-700 text-white pr-10"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {loginError && (
                  <div className="flex items-center gap-2 text-red-400 text-sm" data-testid="login-error">
                    <AlertCircle className="w-4 h-4" />
                    {loginError}
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
                  disabled={loginLoading}
                  data-testid="login-submit-btn"
                >
                  {loginLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LogIn className="w-4 h-4 mr-2" />}
                  Sign In
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register" className="mt-4">
              {wizardSubmitted ? (
                <div className="text-center py-8 space-y-4">
                  <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto animate-bounce" />
                  <h3 className="text-lg font-bold text-white">Registration Request Received!</h3>
                  <p className="text-xs text-slate-400 max-w-md mx-auto leading-relaxed">
                    Thank you for registering <strong>{wizardData.organization_name}</strong>. Your onboarding request has been submitted to the queue.
                  </p>
                  <p className="text-[11px] text-slate-500 max-w-sm mx-auto leading-relaxed">
                    A Priorath super administrator will review your details and initialize your practice. You will receive an activation email shortly.
                  </p>
                  <Button
                    type="button"
                    onClick={() => {
                      setShowLoginModal(false);
                      setWizardStep(1);
                      setWizardSubmitted(false);
                      setWizardData({
                        customer_type: "Independent Practice",
                        organization_name: "",
                        dba_name: "",
                        organization_type: "provider",
                        tax_id: "",
                        organization_npi: "",
                        website: "",
                        organization_phone: "",
                        organization_fax: "",
                        address_line1: "",
                        address_line2: "",
                        city: "",
                        state: "",
                        zip_code: "",
                        country: "US",
                        primary_admin_first_name: "",
                        primary_admin_last_name: "",
                        primary_admin_title: "",
                        primary_admin_email: "",
                        primary_admin_phone: "",
                        practice_type: "Medical",
                        specialty: "",
                        provider_count: 1,
                        location_count: 1,
                        front_office_count: 0,
                        billing_staff_count: 0,
                        estimated_patients_per_day: 10,
                        estimated_monthly_eligibility: 100,
                        estimated_monthly_pa: 10,
                        practice_management_system: "Other",
                        ehr_system: "Other",
                        clearinghouse: "Other",
                        eligibility_vendor: "Other",
                        pa_workflow: "Other",
                        requested_integrations: [],
                        requested_modules: [],
                        go_live_date: "",
                        demo_date: "",
                        notes: "",
                        terms_accepted: false,
                        privacy_accepted: false,
                        baa_required: false,
                        marketing_consent: false
                      });
                    }}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-2 rounded-lg font-semibold text-xs mt-4"
                  >
                    Done
                  </Button>
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <div className="flex justify-between items-center text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-1">
                      <span>{`Step ${wizardStep} of 8: ${
                        [
                          "Organization",
                          "Address",
                          "Primary Administrator",
                          "Practice Details",
                          "Current Systems",
                          "Integrations",
                          "Trial Configuration",
                          "Agreements"
                        ][wizardStep - 1]
                      }`}</span>
                      <span>{Math.round((wizardStep / 8) * 100)}% Complete</span>
                    </div>
                    <div className="w-full bg-slate-800 rounded-full h-1.5 overflow-hidden">
                      <div 
                        className="bg-emerald-500 h-1.5 transition-all duration-300" 
                        style={{ width: `${(wizardStep / 8) * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  <form onSubmit={handleWizardSubmit} className="space-y-4">
                    {wizardStep === 1 && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                          <Label className="text-slate-300 text-xs">Customer Type *</Label>
                          <select
                            value={wizardData.customer_type}
                            onChange={(e) => setWizardData({ ...wizardData, customer_type: e.target.value })}
                            className="w-full bg-slate-800 border border-slate-700 text-white rounded p-2.5 text-xs mt-1 focus:outline-none focus:border-emerald-500"
                          >
                            <option>Independent Practice</option>
                            <option>Multi-location Practice</option>
                            <option>Dental Service Organization (DSO)</option>
                            <option>Medical Group</option>
                            <option>Billing / RCM Company</option>
                            <option>Hospital / Health System</option>
                            <option>Enterprise</option>
                            <option>Other</option>
                          </select>
                        </div>
                        <div className="col-span-2">
                          <Label className="text-slate-300 text-xs">Legal Organization Name *</Label>
                          <Input
                            value={wizardData.organization_name}
                            onChange={(e) => setWizardData({ ...wizardData, organization_name: e.target.value })}
                            placeholder="e.g. Acme Dental Group LLC"
                            className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300 text-xs">DBA Name</Label>
                          <Input
                            value={wizardData.dba_name}
                            onChange={(e) => setWizardData({ ...wizardData, dba_name: e.target.value })}
                            placeholder="e.g. Acme Dental"
                            className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300 text-xs">Organization Type *</Label>
                          <select
                            value={wizardData.organization_type}
                            onChange={(e) => setWizardData({ ...wizardData, organization_type: e.target.value })}
                            className="w-full bg-slate-800 border border-slate-700 text-white rounded p-2.5 text-xs mt-1 focus:outline-none focus:border-emerald-500"
                          >
                            <option value="provider">Provider</option>
                            <option value="tpa">TPA</option>
                            <option value="payer">Payer</option>
                          </select>
                        </div>
                        <div>
                          <Label className="text-slate-300 text-xs">Tax ID / EIN</Label>
                          <Input
                            value={wizardData.tax_id}
                            onChange={(e) => setWizardData({ ...wizardData, tax_id: e.target.value })}
                            placeholder="12-3456789"
                            className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300 text-xs">Organization NPI</Label>
                          <Input
                            value={wizardData.organization_npi}
                            onChange={(e) => setWizardData({ ...wizardData, organization_npi: e.target.value })}
                            placeholder="1999999984"
                            className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
                          />
                        </div>
                        <div className="col-span-2 grid grid-cols-3 gap-4">
                          <div className="col-span-1">
                            <Label className="text-slate-300 text-xs">Website</Label>
                            <Input
                              value={wizardData.website}
                              onChange={(e) => setWizardData({ ...wizardData, website: e.target.value })}
                              placeholder="www.example.com"
                              className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
                            />
                          </div>
                          <div className="col-span-1">
                            <Label className="text-slate-300 text-xs">Phone Number *</Label>
                            <Input
                              value={wizardData.organization_phone}
                              onChange={(e) => setWizardData({ ...wizardData, organization_phone: e.target.value })}
                              placeholder="(555) 019-2834"
                              className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
                            />
                          </div>
                          <div className="col-span-1">
                            <Label className="text-slate-300 text-xs">Fax</Label>
                            <Input
                              value={wizardData.organization_fax}
                              onChange={(e) => setWizardData({ ...wizardData, organization_fax: e.target.value })}
                              placeholder="(555) 019-2835"
                              className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {wizardStep === 2 && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                          <Label className="text-slate-300 text-xs">Address Line 1 *</Label>
                          <Input
                            value={wizardData.address_line1}
                            onChange={(e) => setWizardData({ ...wizardData, address_line1: e.target.value })}
                            placeholder="123 Main St"
                            className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-slate-300 text-xs">Address Line 2</Label>
                          <Input
                            value={wizardData.address_line2}
                            onChange={(e) => setWizardData({ ...wizardData, address_line2: e.target.value })}
                            placeholder="Suite 100"
                            className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300 text-xs">City *</Label>
                          <Input
                            value={wizardData.city}
                            onChange={(e) => setWizardData({ ...wizardData, city: e.target.value })}
                            placeholder="Austin"
                            className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300 text-xs">State *</Label>
                          <Input
                            value={wizardData.state}
                            onChange={(e) => setWizardData({ ...wizardData, state: e.target.value })}
                            placeholder="TX"
                            className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300 text-xs">ZIP Code *</Label>
                          <Input
                            value={wizardData.zip_code}
                            onChange={(e) => setWizardData({ ...wizardData, zip_code: e.target.value })}
                            placeholder="78701"
                            className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300 text-xs">Country *</Label>
                          <Input
                            value={wizardData.country}
                            onChange={(e) => setWizardData({ ...wizardData, country: e.target.value })}
                            placeholder="US"
                            className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
                          />
                        </div>
                      </div>
                    )}

                    {wizardStep === 3 && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-slate-300 text-xs">First Name *</Label>
                          <Input
                            value={wizardData.primary_admin_first_name}
                            onChange={(e) => setWizardData({ ...wizardData, primary_admin_first_name: e.target.value })}
                            placeholder="Jane"
                            className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300 text-xs">Last Name *</Label>
                          <Input
                            value={wizardData.primary_admin_last_name}
                            onChange={(e) => setWizardData({ ...wizardData, primary_admin_last_name: e.target.value })}
                            placeholder="Doe"
                            className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-slate-300 text-xs">Job Title</Label>
                          <Input
                            value={wizardData.primary_admin_title}
                            onChange={(e) => setWizardData({ ...wizardData, primary_admin_title: e.target.value })}
                            placeholder="e.g. Operations Manager"
                            className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300 text-xs">Email *</Label>
                          <Input
                            type="email"
                            value={wizardData.primary_admin_email}
                            onChange={(e) => setWizardData({ ...wizardData, primary_admin_email: e.target.value })}
                            placeholder="jane.doe@example.com"
                            className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300 text-xs">Mobile Phone *</Label>
                          <Input
                            value={wizardData.primary_admin_phone}
                            onChange={(e) => setWizardData({ ...wizardData, primary_admin_phone: e.target.value })}
                            placeholder="(555) 019-2834"
                            className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
                          />
                        </div>
                      </div>
                    )}

                    {wizardStep === 4 && (
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label className="text-slate-300 text-xs">Practice Type *</Label>
                          <select
                            value={wizardData.practice_type}
                            onChange={(e) => setWizardData({ ...wizardData, practice_type: e.target.value })}
                            className="w-full bg-slate-800 border border-slate-700 text-white rounded p-2.5 text-xs mt-1 focus:outline-none focus:border-emerald-500"
                          >
                            <option>Medical</option>
                            <option>Dental</option>
                            <option>Chiropractic</option>
                            <option>Physical Therapy</option>
                            <option>Vision</option>
                            <option>Other</option>
                          </select>
                        </div>
                        <div>
                          <Label className="text-slate-300 text-xs">Specialty</Label>
                          <Input
                            value={wizardData.specialty}
                            onChange={(e) => setWizardData({ ...wizardData, specialty: e.target.value })}
                            placeholder="e.g. General Dentistry"
                            className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300 text-xs">Providers Count *</Label>
                          <Input
                            type="number"
                            min="1"
                            value={wizardData.provider_count}
                            onChange={(e) => setWizardData({ ...wizardData, provider_count: parseInt(e.target.value) || 0 })}
                            className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300 text-xs">Locations Count *</Label>
                          <Input
                            type="number"
                            min="1"
                            value={wizardData.location_count}
                            onChange={(e) => setWizardData({ ...wizardData, location_count: parseInt(e.target.value) || 0 })}
                            className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300 text-xs">Front Office Staff</Label>
                          <Input
                            type="number"
                            min="0"
                            value={wizardData.front_office_count}
                            onChange={(e) => setWizardData({ ...wizardData, front_office_count: parseInt(e.target.value) || 0 })}
                            className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300 text-xs">Billing Staff</Label>
                          <Input
                            type="number"
                            min="0"
                            value={wizardData.billing_staff_count}
                            onChange={(e) => setWizardData({ ...wizardData, billing_staff_count: parseInt(e.target.value) || 0 })}
                            className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300 text-xs">Estimated Patients / Day *</Label>
                          <Input
                            type="number"
                            min="0"
                            value={wizardData.estimated_patients_per_day}
                            onChange={(e) => setWizardData({ ...wizardData, estimated_patients_per_day: parseInt(e.target.value) || 0 })}
                            className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300 text-xs">Eligibility Tx / Month *</Label>
                          <Input
                            type="number"
                            min="0"
                            value={wizardData.estimated_monthly_eligibility}
                            onChange={(e) => setWizardData({ ...wizardData, estimated_monthly_eligibility: parseInt(e.target.value) || 0 })}
                            className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300 text-xs">Prior Auths / Month</Label>
                          <Input
                            type="number"
                            min="0"
                            value={wizardData.estimated_monthly_pa}
                            onChange={(e) => setWizardData({ ...wizardData, estimated_monthly_pa: parseInt(e.target.value) || 0 })}
                            className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
                          />
                        </div>
                      </div>
                    )}

                    {wizardStep === 5 && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-slate-300 text-xs">Practice Management System</Label>
                          <select
                            value={wizardData.practice_management_system}
                            onChange={(e) => setWizardData({ ...wizardData, practice_management_system: e.target.value })}
                            className="w-full bg-slate-800 border border-slate-700 text-white rounded p-2.5 text-xs mt-1 focus:outline-none focus:border-emerald-500"
                          >
                            <option>Open Dental</option>
                            <option>Dentrix</option>
                            <option>Eaglesoft</option>
                            <option>Athena</option>
                            <option>eClinicalWorks</option>
                            <option>Epic</option>
                            <option>NextGen</option>
                            <option>DrChrono</option>
                            <option>Tebra</option>
                            <option>AdvancedMD</option>
                            <option>Practice Fusion</option>
                            <option>Other</option>
                          </select>
                        </div>
                        <div>
                          <Label className="text-slate-300 text-xs">EHR / EMR System</Label>
                          <select
                            value={wizardData.ehr_system}
                            onChange={(e) => setWizardData({ ...wizardData, ehr_system: e.target.value })}
                            className="w-full bg-slate-800 border border-slate-700 text-white rounded p-2.5 text-xs mt-1 focus:outline-none focus:border-emerald-500"
                          >
                            <option>Open Dental</option>
                            <option>Dentrix</option>
                            <option>Eaglesoft</option>
                            <option>Athena</option>
                            <option>eClinicalWorks</option>
                            <option>Epic</option>
                            <option>NextGen</option>
                            <option>DrChrono</option>
                            <option>Tebra</option>
                            <option>AdvancedMD</option>
                            <option>Practice Fusion</option>
                            <option>Other</option>
                          </select>
                        </div>
                        <div>
                          <Label className="text-slate-300 text-xs">Clearinghouse</Label>
                          <select
                            value={wizardData.clearinghouse}
                            onChange={(e) => setWizardData({ ...wizardData, clearinghouse: e.target.value })}
                            className="w-full bg-slate-800 border border-slate-700 text-white rounded p-2.5 text-xs mt-1 focus:outline-none focus:border-emerald-500"
                          >
                            <option>Stedi</option>
                            <option>DentalXChange</option>
                            <option>Change Healthcare</option>
                            <option>Waystar</option>
                            <option>Availity</option>
                            <option>Office Ally</option>
                            <option>TriZetto</option>
                            <option>Other</option>
                          </select>
                        </div>
                        <div>
                          <Label className="text-slate-300 text-xs">Current Eligibility Vendor</Label>
                          <select
                            value={wizardData.eligibility_vendor}
                            onChange={(e) => setWizardData({ ...wizardData, eligibility_vendor: e.target.value })}
                            className="w-full bg-slate-800 border border-slate-700 text-white rounded p-2.5 text-xs mt-1 focus:outline-none focus:border-emerald-500"
                          >
                            <option>Stedi</option>
                            <option>DentalXChange</option>
                            <option>Change Healthcare</option>
                            <option>Waystar</option>
                            <option>Availity</option>
                            <option>Office Ally</option>
                            <option>TriZetto</option>
                            <option>Other</option>
                          </select>
                        </div>
                        <div className="col-span-2">
                          <Label className="text-slate-300 text-xs">Current Prior Authorization Process</Label>
                          <select
                            value={wizardData.pa_workflow}
                            onChange={(e) => setWizardData({ ...wizardData, pa_workflow: e.target.value })}
                            className="w-full bg-slate-800 border border-slate-700 text-white rounded p-2.5 text-xs mt-1 focus:outline-none focus:border-emerald-500"
                          >
                            <option>Manual</option>
                            <option>Availity</option>
                            <option>CoverMyMeds</option>
                            <option>Fax</option>
                            <option>Portal</option>
                            <option>Other</option>
                          </select>
                        </div>
                      </div>
                    )}

                    {wizardStep === 6 && (
                      <div className="space-y-3">
                        <Label className="text-slate-300 text-xs block mb-2 font-semibold">Select Requested Integrations</Label>
                        <div className="grid grid-cols-2 gap-3">
                          {["Open Dental", "Dentrix", "CRMBridge", "Eaglesoft", "Availity", "Stedi", "FHIR"].map((integration) => {
                            const checked = wizardData.requested_integrations.includes(integration);
                            return (
                              <label key={integration} className="flex items-center gap-2.5 p-3.5 bg-slate-800/40 hover:bg-slate-800/80 rounded-xl border border-slate-700/60 cursor-pointer text-slate-300 hover:text-white transition-all">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => {
                                    const nextVal = checked
                                      ? wizardData.requested_integrations.filter(i => i !== integration)
                                      : [...wizardData.requested_integrations, integration];
                                    setWizardData({ ...wizardData, requested_integrations: nextVal });
                                  }}
                                  className="accent-emerald-500 rounded"
                                />
                                <span className="text-xs ml-1.5">{integration}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {wizardStep === 7 && (
                      <div className="space-y-4">
                        <div>
                          <Label className="text-slate-300 text-xs block mb-2 font-semibold">Interested Modules</Label>
                          <div className="grid grid-cols-2 gap-2.5">
                            {[
                              "Coverage Intelligence",
                              "Eligibility Intelligence",
                              "Benefits Intelligence",
                              "Drug Formulary Intelligence",
                              "Medical Policy Intelligence",
                              "Prior Authorization",
                              "Referral Intelligence",
                              "AI Copilot"
                            ].map((module) => {
                              const checked = wizardData.requested_modules.includes(module);
                              return (
                                <label key={module} className="flex items-center gap-2 p-2 bg-slate-800/40 hover:bg-slate-800 rounded border border-slate-700/50 cursor-pointer text-slate-300 hover:text-white transition-colors">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => {
                                      const nextVal = checked
                                        ? wizardData.requested_modules.filter(m => m !== module)
                                        : [...wizardData.requested_modules, module];
                                      setWizardData({ ...wizardData, requested_modules: nextVal });
                                    }}
                                    className="accent-emerald-500 rounded"
                                  />
                                  <span className="text-[11px] ml-1">{module}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-slate-300 text-xs">Expected Go-live Date</Label>
                            <Input
                              type="date"
                              value={wizardData.go_live_date}
                              onChange={(e) => setWizardData({ ...wizardData, go_live_date: e.target.value })}
                              className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-slate-300 text-xs">Preferred Demo Date</Label>
                            <Input
                              type="date"
                              value={wizardData.demo_date}
                              onChange={(e) => setWizardData({ ...wizardData, demo_date: e.target.value })}
                              className="bg-slate-800 border-slate-700 text-white text-xs mt-1"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-slate-300 text-xs">Additional Notes / Trial Requirements</Label>
                          <textarea
                            value={wizardData.notes}
                            onChange={(e) => setWizardData({ ...wizardData, notes: e.target.value })}
                            placeholder="Any specific workflows, integrations, or demo focus areas..."
                            className="w-full bg-slate-800 border border-slate-700 rounded p-2 text-xs text-white mt-1 h-16 placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                          />
                        </div>
                      </div>
                    )}

                    {wizardStep === 8 && (
                      <div className="space-y-4">
                        <span className="block text-xs font-semibold text-emerald-400">Legal Agreements & Consent</span>
                        <div className="space-y-3">
                          <label className="flex items-start gap-2.5 p-2 bg-slate-800/30 border border-slate-700/50 rounded cursor-pointer hover:bg-slate-800/50 transition-colors">
                            <input
                              type="checkbox"
                              checked={wizardData.terms_accepted}
                              onChange={(e) => setWizardData({ ...wizardData, terms_accepted: e.target.checked })}
                              className="accent-emerald-500 mt-0.5"
                            />
                            <span className="text-[11px] text-slate-300">I accept the Terms and Conditions for Sandbox Access *</span>
                          </label>
                          <label className="flex items-start gap-2.5 p-2 bg-slate-800/30 border border-slate-700/50 rounded cursor-pointer hover:bg-slate-800/50 transition-colors">
                            <input
                              type="checkbox"
                              checked={wizardData.privacy_accepted}
                              onChange={(e) => setWizardData({ ...wizardData, privacy_accepted: e.target.checked })}
                              className="accent-emerald-500 mt-0.5"
                            />
                            <span className="text-[11px] text-slate-300">I accept the Privacy Policy *</span>
                          </label>
                          <label className="flex items-start gap-2.5 p-2 bg-slate-800/30 border border-slate-700/50 rounded cursor-pointer hover:bg-slate-800/50 transition-colors">
                            <input
                              type="checkbox"
                              checked={wizardData.baa_required}
                              onChange={(e) => setWizardData({ ...wizardData, baa_required: e.target.checked })}
                              className="accent-emerald-500 mt-0.5"
                            />
                            <span className="text-[11px] text-slate-300">HIPAA Business Associate Agreement (BAA) Required *</span>
                          </label>
                          <label className="flex items-start gap-2.5 p-2 bg-slate-800/30 border border-slate-700/50 rounded cursor-pointer hover:bg-slate-800/50 transition-colors">
                            <input
                              type="checkbox"
                              checked={wizardData.marketing_consent}
                              onChange={(e) => setWizardData({ ...wizardData, marketing_consent: e.target.checked })}
                              className="accent-emerald-500 mt-0.5"
                            />
                            <span className="text-[11px] text-slate-300">Receive periodic product updates and newsletter</span>
                          </label>
                        </div>
                      </div>
                    )}

                    <div className="flex justify-between items-center pt-4 border-t border-slate-800 mt-4">
                      {wizardStep > 1 ? (
                        <Button
                          type="button"
                          onClick={() => setWizardStep(wizardStep - 1)}
                          className="bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold px-4 py-2 rounded"
                        >
                          &larr; Back
                        </Button>
                      ) : (
                        <div></div>
                      )}

                      {wizardStep < 8 ? (
                        <Button
                          type="button"
                          onClick={() => {
                            if (validateStep(wizardStep)) {
                              setWizardStep(wizardStep + 1);
                            }
                          }}
                          className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold px-5 py-2 rounded ml-auto flex items-center gap-1"
                        >
                          Next &rarr;
                        </Button>
                      ) : (
                        <Button
                          type="submit"
                          disabled={wizardSubmitting}
                          className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold px-5 py-2 rounded ml-auto flex items-center gap-1 shadow shadow-emerald-500/20"
                        >
                          {wizardSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                          Submit Registration Request
                        </Button>
                      )}
                    </div>
                  </form>
                </>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}
